import { Component, DestroyRef, ElementRef, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ClientDashboardMetadata, ClientLayout, ClientLayoutItemType } from '@mmlogic/components';
import { ApiClientService } from 'src/app/core/services/api-client.service';
import { NotificationService } from 'src/app/core/services/notification.service';

@Component({
    selector: 'app-import-file',
    templateUrl: './import-file.component.html',
    styleUrls: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ImportFileComponent implements OnInit {
  clientLayout: ClientLayout | null = null;
  tenantCode: string;

  @ViewChild('mrdFormEl') mrdFormEl: ElementRef;

  constructor(
    private api: ApiClientService,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
    private router: Router,
    private destroyRef: DestroyRef) { }

  ngOnInit(): void {
    // The tenant first: system metadata is published per tenant, so the request needs it.
    this.refresh();
    this.api.getSystemForm(this.tenantCode, "importFiles").subscribe(metadata => this.setMetadata(metadata));
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.refresh());
  }

  refresh() {
    this.tenantCode = this.route.snapshot.params['tenantCode'];
  }

  private setMetadata(metadata: ClientDashboardMetadata): void {
    const allItems = metadata.layouts.flatMap((layout) => {
      if (layout.tabPage && layout.label) {
        return [{ type: ClientLayoutItemType.SECTION, label: layout.label, items: layout.items }];
      }
      return layout.items;
    });
    this.clientLayout = { items: allItems };
  }

  onMrdSubmit(event: CustomEvent<Record<string, unknown>>): void {
    this.api.importFile(this.tenantCode, event.detail).subscribe({
      next: location => this.router.navigate([location]),
      // Stay on the form so the user can correct the file and retry.
      error: error => this.notificationService.showServerError(
        error, 'import.failed', 'WARNING'),
    });
  }

  onMrdUpload(event: CustomEvent<{ name: string; file: File }>): void {
    const { name, file } = event.detail;
    this.api.upload(this.tenantCode, file).subscribe({
      // Echo the server's own fileName/contentType/size back untouched — claimBinary
      // on submit verifies it against what it recorded at upload time (TASK-0351).
      next: uploaded => this.mrdFormEl.nativeElement.setFieldValue(name, uploaded),
      error: error => this.notificationService.showServerError(
        error, 'import.upload_failed', 'WARNING'),
    });
  }

  onCancel(): void {
    this.router.navigate(['..'], { relativeTo: this.route });
  }
}
